import { FlaskConical } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { MemoryResponse } from '../../shared/api.ts';
import { JOURNEY_STAGES, type JourneyStage } from '../../shared/memory.ts';
import { api } from '../lib/api.ts';
import { cn } from '../lib/cn.ts';
import type { AdminMode } from '../shell/adminMode.ts';
import { useMemory } from '../shell/useMemory.tsx';
import { MocksPanel } from './MocksPanel.tsx';

// `new` is "Reset me".
const STAGE_LABELS: Record<Exclude<JourneyStage, 'new'>, string> = {
  call1_done: 'After call 1',
  context_ready: 'Context ready',
  assessed: 'Assessed',
};
const STAGES = JOURNEY_STAGES.filter((s): s is Exclude<JourneyStage, 'new'> => s !== 'new');

/**
 * Admin tools. Shown only to ADMIN_EMAILS accounts; the server checks too. Stage jumps and the memory editor act on
 * the admin's test account (ADR-0014), so they show only while it's on; reset works on either account.
 */
export function AdminPanel({
  admin,
  initial,
}: {
  admin: AdminMode;
  /** For the Lab: start with a button armed or a status shown. */
  initial?: { armed?: string; status?: { tone: 'good' | 'bad'; text: string } };
}) {
  const { memory, set } = useMemory();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: 'good' | 'bad'; text: string } | null>(initial?.status ?? null);
  const [json, setJson] = useState<{ text: string; basedOn: number } | null>(null);
  const details = useRef<HTMLDetailsElement>(null);
  // These replace the account's memory, so each takes a second tap within a few seconds.
  const [armed, setArmed] = useState<string | null>(initial?.armed ?? null);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(null), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  const run = (label: string, action: () => Promise<MemoryResponse>) => {
    setBusy(true);
    setArmed(null);
    setStatus(null);
    action()
      .then((r) => {
        set(r.memory);
        if (details.current) details.current.open = false;
        setStatus({ tone: 'good', text: label });
      })
      .catch((e: Error) => setStatus({ tone: 'bad', text: e.message }))
      .finally(() => setBusy(false));
  };
  const confirm = (key: string, label: string, action: () => Promise<MemoryResponse>) =>
    armed === key ? run(label, action) : setArmed(key);

  // Turning admin mode off leaves the test account first; if the switch is refused (a call is running there), the
  // mode stays on and says why, rather than leaving a user view on the test account.
  const toggleMode = () => {
    if (!admin.on || !admin.sandbox) return admin.set(!admin.on);
    setBusy(true);
    setStatus(null);
    admin
      .setSandbox(false)
      .then(() => admin.set(false))
      .catch((e: Error) => setStatus({ tone: 'bad', text: e.message }))
      .finally(() => setBusy(false));
  };

  // On success the app reloads for the other account, and this panel with it.
  const switchAccount = () => {
    setBusy(true);
    setArmed(null);
    setStatus(null);
    admin
      .setSandbox(!admin.sandbox)
      .catch((e: Error) => setStatus({ tone: 'bad', text: e.message }))
      .finally(() => setBusy(false));
  };

  const account = admin.sandbox ? 'test' : 'own';
  const saveJson = () => {
    if (!json) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(json.text);
    } catch {
      setStatus({ tone: 'bad', text: 'That isn’t valid JSON.' });
      return;
    }
    run('Memory saved.', () => api.adminSetMemory(parsed, json.basedOn, account));
  };

  return (
    <section data-testid="admin-panel" className="card space-y-4 ring-1 ring-warn/30">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-warn">
          <FlaskConical className="size-4" aria-hidden /> Admin
        </h2>
        <button
          type="button"
          role="switch"
          aria-checked={admin.on}
          data-testid="admin-mode"
          disabled={busy}
          onClick={toggleMode}
          className="flex min-h-11 items-center gap-2 text-sm text-muted"
        >
          Admin mode
          <Toggle on={admin.on} />
        </button>
      </div>
      {admin.on ? (
        <>
          <div className="space-y-2">
            <button
              type="button"
              role="switch"
              aria-checked={admin.sandbox}
              disabled={busy}
              data-testid="admin-sandbox"
              onClick={switchAccount}
              className="flex min-h-11 items-center gap-2 text-sm text-fg disabled:opacity-40"
            >
              <Toggle on={admin.sandbox} />
              Test account
            </button>
            <p className="text-sm text-muted">
              {admin.sandbox
                ? 'Calls, answers and memory go to a separate test account. Jump it to a journey stage: its memory is replaced with the Lab’s mock data, so each button asks for a second tap.'
                : 'You’re on your own account. Turn on the test account to jump to a journey stage, edit memory as JSON or use mocks, and your own memory stays as it is.'}
            </p>
            {admin.sandbox ? (
              <div className="flex flex-wrap gap-2">
                {STAGES.map((stage) => (
                  <button
                    key={stage}
                    type="button"
                    disabled={busy}
                    data-testid={`admin-stage-${stage}`}
                    onClick={() =>
                      confirm(stage, `Now at “${STAGE_LABELS[stage]}”.`, () => api.adminSetStage(stage, account))
                    }
                    className={cn(
                      'min-h-11 rounded-full px-4 text-sm ring-1 transition disabled:opacity-40',
                      armed === stage
                        ? 'text-warn ring-warn'
                        : memory.journey.stage === stage
                          ? 'bg-fg text-bg ring-fg'
                          : 'text-muted ring-fg/15 hover:text-fg',
                    )}
                  >
                    {armed === stage ? 'Tap again to replace' : STAGE_LABELS[stage]}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              data-testid="admin-reset"
              onClick={() => confirm('reset', 'Reset to a new user.', () => api.adminSetStage('new', account))}
              className={cn(
                'min-h-11 rounded-full px-4 text-sm text-bad ring-1 transition hover:bg-bad/10 disabled:opacity-40',
                armed === 'reset' ? 'ring-bad' : 'ring-bad/30',
              )}
            >
              {armed === 'reset' ? 'Tap again to reset' : admin.sandbox ? 'Reset test account' : 'Reset me'}
            </button>
            <a
              href="/admin"
              data-testid="admin-page-link"
              className="flex min-h-11 items-center rounded-full px-4 text-sm text-accent ring-1 ring-accent/30"
            >
              Modules and flags
            </a>
            <a
              href="/lab"
              data-testid="admin-lab"
              className="flex min-h-11 items-center rounded-full px-4 text-sm text-accent ring-1 ring-accent/30"
            >
              Open the Lab
            </a>
          </div>
          {admin.sandbox ? (
            <details
              ref={details}
              data-testid="admin-json"
              onToggle={(e) => {
                if (!(e.target as HTMLDetailsElement).open) return setJson(null);
                // Fresh from the server: this tab's copy can be behind (a call finished on another device), and a
                // save based on it would be refused every time.
                api.memory().then(
                  (r) => {
                    set(r.memory);
                    setJson({ text: JSON.stringify(r.memory, null, 2), basedOn: r.memory.version });
                  },
                  (err: Error) => setStatus({ tone: 'bad', text: err.message }),
                );
              }}
            >
              <summary className="cursor-pointer text-sm text-muted">Memory as JSON</summary>
              {json !== null ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    data-testid="admin-json-text"
                    value={json.text}
                    onChange={(e) => setJson({ ...json, text: e.target.value })}
                    spellCheck={false}
                    rows={14}
                    className="w-full rounded-xl bg-fg/5 p-3 font-mono text-xs text-fg ring-1 ring-fg/15 focus:outline-none focus:ring-accent/50"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    data-testid="admin-json-save"
                    onClick={saveJson}
                    className="min-h-11 rounded-full bg-fg px-4 text-sm font-semibold text-bg disabled:opacity-40"
                  >
                    Save JSON
                  </button>
                </div>
              ) : null}
            </details>
          ) : null}
          {/* Mounted before it has text, so screen readers announce the result. */}
          <p
            role="status"
            data-testid="admin-status"
            className={cn('text-sm', status?.tone === 'bad' ? 'text-bad' : 'text-good')}
          >
            {status?.text}
          </p>
          {admin.sandbox ? <MocksPanel /> : null}
        </>
      ) : (
        <p className="text-sm text-muted">
          {admin.sandbox
            ? 'Off, but this browser is still on the test account. Turn admin mode on and switch the test account off.'
            : 'Off: you see exactly what users see.'}
        </p>
      )}
    </section>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <span className={cn('relative h-6 w-10 rounded-full transition', on ? 'bg-warn' : 'bg-fg/15')}>
      <span
        className={cn(
          'absolute top-0.5 size-5 rounded-full bg-surface shadow transition-all',
          on ? 'left-[18px]' : 'left-0.5',
        )}
      />
    </span>
  );
}
